import { NavBar } from '@design-system/NavBar';
import { StandardContainer } from '@design-system/StandardContainer';
import { ContributingScoresSection } from '@screens/LevelTwoDetail/ContributingScores';
import { DoctorsNotes } from '@screens/LevelTwoDetail/DoctorsNotes';
import { ImproveHealthSection } from '@screens/LevelTwoDetail/ImproveHealthSection';
import { L2HeaderSection } from '@screens/LevelTwoDetail/L2Header';

import {
  BoosterAndBlockerSummaryResponse,
  CommonlyLinkedSymptoms,
  IDoctorsNoteResponse,
  IScoreDetailResponse,
} from '@viome/viome-base/src/usecase/Health/Health.model';

import { ConditionalRender } from '@/components/Helpers/ComponentRender/ConditionalRender';
import { Render } from '@/components/Helpers/ComponentRender/Render';
import DoctorsNoteShimmer from '@/components/screens/LevelTwoDetail/DoctorsNoteShimmer';
import { gettingPlaceholderWithScoreId } from '@/components/utils/utils';
import { sanitizeString } from '@/utils/string/sanitizeString';
import ImproveMyScoreShimmer from '@screens/LevelTwoDetail/ImproveMyScoreShimmer';
import { NutritionJourneyDeeperSection } from '@screens/NutritionPage/NutritionJourneyDeeperSection';

interface L2DetailProps {
  score: IScoreDetailResponse;
  doctorNotes?: IDoctorsNoteResponse;
  boosters?: BoosterAndBlockerSummaryResponse;
  isHeaderLoading: boolean;
  isDoctorsNoteLoading: boolean;
  isBoostersLoading: boolean;
  isHeaderError: boolean;
  isBoosterError: boolean;
  isDoctorError: boolean;
  scoreId: number;
  commonlyLinkedSymptoms?: CommonlyLinkedSymptoms;
  'data-testid'?: string;
}

{
  /* TODO: Rename the file based on component name */
}
export const L2DetailContainer: React.FC<L2DetailProps> = ({
  score,
  doctorNotes,
  boosters,
  isHeaderLoading,
  isBoostersLoading,
  isDoctorsNoteLoading,
  isBoosterError,
  isDoctorError,
  isHeaderError,
  scoreId,
  commonlyLinkedSymptoms,
  'data-testid': testId,
}) => {
  const sanitizedScoreRank = sanitizeString(score?.rank, {
    lowerCase: true,
  });

  const renderDoctorsNote = (shimmer: boolean) => {
    return shimmer ? (
      <StandardContainer>
        <DoctorsNoteShimmer data-testid="healthL2Details-doctorsNoteShimmer" />
      </StandardContainer>
    ) : (
      <section
        className="bg-black bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(to bottom, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0) 30%, rgba(0, 0, 0, 1) 100%), url('https://cdn-st.stage.azure.viome.com/viome-cdn/media/app/v2/png/home/image_for_doctors_notes.png')`,
        }}
        data-testid="healthL2Details-doctorsNoteSection"
      >
        <StandardContainer>
          <DoctorsNotes notes={doctorNotes || []} data-testid="healthL2Details-doctorsNotes" />
        </StandardContainer>
      </section>
    );
  };

  return (
    <div className="min-h-screen bg-black text-white pb-12" data-testid={testId}>
      {/* Header Section */}
      <section
        data-product={sanitizedScoreRank}
        className="transition-colors h-full bg-gradient-to-b data-[product='attention']:from-[#B18383] data-[product='attention']:to-[#000] data-[product='maintain']:from-[#87B9B0E5] data-[product='maintain']:to-[#000] data-[product='improve']:from-[#B9B687E5] data-[product='improve']:to-[#000] from-[#000] to-[#000]"
        data-testid="healthL2Details-headerSection"
      >
        <StandardContainer>
          <NavBar returnButtonTitle="Health" className="md:-ml-5 mt-8" data-testid="healthL2Details-navbar" />
          <L2HeaderSection
            l2Score={score}
            isLoading={isHeaderLoading}
            commonlyLinkedSymptoms={commonlyLinkedSymptoms}
            scoreId={scoreId}
            data-testid="healthL2Details-header"
          />
        </StandardContainer>
      </section>

      {/* Doctor's Notes Section */}
      <ConditionalRender
        conditions={[
          { when: isDoctorsNoteLoading, render: renderDoctorsNote(true) },
          { when: isDoctorError, render: null },
        ]}
        otherwise={renderDoctorsNote(false)}
      />

      <StandardContainer>
        {/* Contributing Scores */}
        <ConditionalRender
          conditions={[{ when: isHeaderError, render: null }]}
          otherwise={
            <ContributingScoresSection
              contributingScoresCount={score?.contributing_scores || 0}
              isLoading={isHeaderLoading}
              scoreId={scoreId}
              title={score.title ?? ''}
              data-testid="healthL2Details-contributingScores"
            />
          }
        />

        {/* Improve Gut & Digestive Health */}
        <ConditionalRender
          conditions={[
            { when: isBoosterError, render: null },
            { when: isBoostersLoading, render: <ImproveMyScoreShimmer data-testid="healthL2Details-improveScoreShimmer" /> },
          ]}
          otherwise={
            <ImproveHealthSection
              scoreName={score.title}
              boosters={boosters}
              scoreId={scoreId}
              data-testid="healthL2Details-improveHealthSection"
            />
          }
        />
        <Render when={scoreId !== undefined}>
          <div className="mb-12" data-testid="healthL2Details-journeyDeeperSection">
            <NutritionJourneyDeeperSection
              sectionInfo={gettingPlaceholderWithScoreId(scoreId)}
              screen="healthZone"
              data-testid="healthL2Details-journeyDeeper"
            />
          </div>
        </Render>
      </StandardContainer>
    </div>
  );
};
